import React, { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { schema as billingSchema } from './BillingForm'

import Col from 'react-bootstrap/Col'
import Row from 'react-bootstrap/Row'
import Form from 'react-bootstrap/Form'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import CountrySelect from './CountrySelect'

import { wpDebug } from 'script-params'

const simpleSchema = z.object({
  firstName: z.string().min(1, { message: 'Vorname muss angegeben werden' }),
  lastName: z.string().min(1, { message: 'Nachname muss angegeben werden' }),
  email: z
    .string()
    .email({ message: 'Eine gültige E-Mail Adresse muss angegeben werden' }),
  phone: z.string(),
})

const exampleData = {
  firstName: 'John',
  lastName: 'Dorian',
  email: 'john@example.com',
  phone: '',
  separateBilling: false,
  companyName: '',
  country: 'DE',
  city: '',
  zip: '',
  address1: '',
  address2: '',
}

const getDefaultValues = (initialState = {}) => {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    separateBilling: false,
    companyName: '',
    country: 'DE',
    city: '',
    zip: '',
    address1: '',
    address2: '',
    ...(wpDebug ? exampleData : {}),
    ...initialState,
  }
}

const AttendeeForm = ({
  formId,
  initialState,
  onSubmit,
  separateBillingPossible,
}) => {
  const [separateBilling, setSeparateBilling] = useState(
    initialState?.separateBilling ?? false
  )
  const schema = separateBilling ? billingSchema : simpleSchema
  const {
    handleSubmit,
    control,
    formState: { isSubmitted },
  } = useForm({
    defaultValues: getDefaultValues(initialState),
    resolver: zodResolver(schema),
  })

  return (
    <Form
      id={formId}
      noValidate
      onSubmit={handleSubmit((form) => onSubmit({ ...form, separateBilling }))}
    >
      <Row className="g-4">
        <Controller
          name="firstName"
          control={control}
          render={({
            field: { ref, value, onChange, name },
            fieldState: { invalid, error },
          }) => (
            <Form.Group as={Col} sm className="mb-3" controlId={name}>
              <FloatingLabel controlId={name} label="Vorname" className="mb-3">
                <Form.Control
                  placeholder="..."
                  ref={ref}
                  value={value}
                  onChange={onChange}
                  onBlur={(e) => onChange(e.currentTarget.value.trim())}
                  type="text"
                  isValid={!invalid && isSubmitted}
                  isInvalid={invalid}
                />
                <Form.Control.Feedback type="invalid">
                  {error?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>
          )}
        />
        <Controller
          name="lastName"
          control={control}
          render={({
            field: { ref, value, onChange, name },
            fieldState: { invalid, error },
          }) => (
            <Form.Group as={Col} sm className="mb-3" controlId={name}>
              <FloatingLabel controlId={name} label="Nachname" className="mb-3">
                <Form.Control
                  placeholder="..."
                  ref={ref}
                  value={value}
                  onChange={onChange}
                  onBlur={(e) => onChange(e.currentTarget.value.trim())}
                  type="text"
                  isValid={!invalid && isSubmitted}
                  isInvalid={invalid}
                />
                <Form.Control.Feedback type="invalid">
                  {error?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>
          )}
        />
      </Row>

      <Row className="g-4">
        <Controller
          name="email"
          control={control}
          render={({
            field: { ref, value, onChange, name },
            fieldState: { invalid, error },
          }) => (
            <Form.Group as={Col} sm className="mb-3" controlId={name}>
              <FloatingLabel controlId={name} label="E-Mail" className="mb-3">
                <Form.Control
                  placeholder="..."
                  ref={ref}
                  value={value}
                  onChange={onChange}
                  onBlur={(e) => onChange(e.currentTarget.value.trim())}
                  type="email"
                  isValid={!invalid && isSubmitted}
                  isInvalid={invalid}
                />
                <Form.Control.Feedback type="invalid">
                  {error?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>
          )}
        />
        <Controller
          name="phone"
          control={control}
          render={({
            field: { ref, value, onChange, name },
            fieldState: { invalid, error },
          }) => (
            <Form.Group as={Col} sm className="mb-3" controlId={name}>
              <FloatingLabel controlId={name} label="Telefon" className="mb-3">
                <Form.Control
                  placeholder="..."
                  ref={ref}
                  value={value}
                  onChange={onChange}
                  onBlur={(e) => onChange(e.currentTarget.value.trim())}
                  type="text"
                  isValid={!invalid && isSubmitted}
                  isInvalid={invalid}
                />
                <Form.Control.Feedback type="invalid">
                  {error?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>
          )}
        />
      </Row>

      {separateBillingPossible && (
        <>
          <Row className="g-4">
            <Form.Group as={Col}>
              <Form.Check
                type="switch"
                checked={separateBilling}
                onChange={() => setSeparateBilling((val) => !val)}
                label="separate Rechnung"
              />
            </Form.Group>
          </Row>
        </>
      )}

      {/* <Controller
          name="separateBilling"
          control={control}
          render={({ field: { value, onChange, name } }) => (
            <Form.Group as={Col}>
              <Form.Check
                type="switch"
                checked={value}
                onChange={onChange}
                label="separate Rechnung"
              />
            </Form.Group>
          )}
        /> */}

      {separateBilling && (
        <>
          <Row className="g-4">
            <Controller
              name="country"
              control={control}
              render={({
                field: { ref, value, onChange, name },
                fieldState: { invalid, error },
              }) => (
                <>
                  <Form.Group as={Col} sm className="mb-3" controlId={name}>
                    <Form.Label>Land</Form.Label>
                    <CountrySelect
                      ref={ref}
                      value={value}
                      onChange={onChange}
                    />
                  </Form.Group>
                </>
              )}
            />
            <Col sm />
          </Row>

          <Row className="g-4">
            <Controller
              name="zip"
              control={control}
              render={({
                field: { ref, value, onChange, name },
                fieldState: { invalid, error },
              }) => (
                <Form.Group as={Col} sm className="mb-3" controlId={name}>
                  <FloatingLabel
                    controlId={name}
                    label="Postleitzahl"
                    className="mb-3"
                  >
                    <Form.Control
                      placeholder="..."
                      ref={ref}
                      value={value}
                      onChange={onChange}
                      onBlur={(e) => onChange(e.currentTarget.value.trim())}
                      type="text"
                      isValid={!invalid && isSubmitted}
                      isInvalid={invalid}
                    />
                    <Form.Control.Feedback type="invalid">
                      {error?.message}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              )}
            />
            <Controller
              name="city"
              control={control}
              render={({
                field: { ref, value, onChange, name },
                fieldState: { invalid, error },
              }) => (
                <Form.Group as={Col} sm className="mb-3" controlId={name}>
                  <FloatingLabel
                    controlId={name}
                    label="Stadt"
                    className="mb-3"
                  >
                    <Form.Control
                      placeholder="..."
                      ref={ref}
                      value={value}
                      onChange={onChange}
                      onBlur={(e) => onChange(e.currentTarget.value.trim())}
                      type="text"
                      isValid={!invalid && isSubmitted}
                      isInvalid={invalid}
                    />
                    <Form.Control.Feedback type="invalid">
                      {error?.message}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              )}
            />
          </Row>
          <Row className="g-4">
            <Controller
              name="address1"
              control={control}
              render={({
                field: { ref, value, onChange, name },
                fieldState: { invalid, error },
              }) => (
                <Form.Group as={Col} sm className="mb-3" controlId={name}>
                  <FloatingLabel
                    controlId={name}
                    label="Straße und Hausnummer"
                    className="mb-3"
                  >
                    <Form.Control
                      placeholder="..."
                      ref={ref}
                      value={value}
                      onChange={onChange}
                      onBlur={(e) => onChange(e.currentTarget.value.trim())}
                      type="text"
                      isValid={!invalid && isSubmitted}
                      isInvalid={invalid}
                    />
                    <Form.Control.Feedback type="invalid">
                      {error?.message}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              )}
            />
            <Controller
              name="address2"
              control={control}
              render={({
                field: { ref, value, onChange, name },
                fieldState: { invalid, error },
              }) => (
                <Form.Group as={Col} sm className="mb-3" controlId={name}>
                  <FloatingLabel
                    controlId={name}
                    label="Zusatz"
                    className="mb-3"
                  >
                    <Form.Control
                      placeholder="..."
                      ref={ref}
                      value={value}
                      onChange={onChange}
                      onBlur={(e) => onChange(e.currentTarget.value.trim())}
                      type="text"
                    />
                  </FloatingLabel>
                </Form.Group>
              )}
            />
          </Row>
        </>
      )}
    </Form>
  )
}

AttendeeForm.defaultProps = {
  separateBillingPossible: false,
}

export default AttendeeForm
